import { Button, Tag, TagGroup } from '@fluentui/react-components'
import { Delete24Regular, Edit24Regular, PersonRegular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ConfirmDialog } from '../Dialog/ConfirmDialog'
import { ComposedPopover } from '../Popover/ComposedPopover'
import { AddUserToWorkspaceForm } from '../SubscriptionControl/AddUsersToWorkspaceForm'
import './DirectLinkedUserCard.scss'

type MessagesProjectsProjectUser = RestSchemas['Messages.Projects.ProjectUser']

type DirectLinkedUserCardProps = {
  user: MessagesProjectsProjectUser
  editEnabled: boolean
  projectId: number
  orgId: number
  appId: number
  workspaceId: number
}

export const DirectLinkedUserCard: React.FC<DirectLinkedUserCardProps> = ({
  user,
  editEnabled,
  projectId,
  orgId,
  appId,
  workspaceId,
}) => {
  const [manageRolesOpen, setManageRolesOpen] = useState(false)
  const closeManageRoles = useCallback(() => setManageRolesOpen(false), [])
  const queryClient = useQueryClient()
  const { getWorkspacesApiGetUsers, deleteProjectsApiRemoveUserFromProject } = createRestClient()

  const invalidateProjectUsers = () => {
    getWorkspacesApiGetUsers.invalidateQueries({ parameters: { query: { workspaceId } } }, queryClient)
  }

  const { mutate: deleteUser /* isPending: deleteUserPending */ } = deleteProjectsApiRemoveUserFromProject.useMutation()

  const handleDeleteUser = (user: RestSchemas['Messages.Projects.ProjectUser']) => {
    const mappedRoles = user.memberRoles.map((role) => ({
      roleId: role.roleId,
      projectId: projectId || 0,
      userId: user.id,
    }))
    deleteUser(
      { body: mappedRoles },
      {
        onSuccess: () => {
          toast.success('User removed successfully.')
          // Invalidate the query to refresh the users list
          invalidateProjectUsers()
        },
        onError: () => {
          toast.error('Failed to remove user.')
        },
      }
    )
  }

  const icon = (
    <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-grey-lightest p-2 text-grey-dark">
      <PersonRegular fontSize={34} />
    </div>
  )

  const titleDescription = (
    <div className="flex flex-col justify-between gap-1">
      <div className="text-base">
        {user.givenName} {user.familyName}
      </div>
      <div className="text-xs font-light">{user.email}</div>
    </div>
  )

  const footer = (
    <div className="col-span-2 col-start-2 flex flex-row justify-between gap-2 border-t pt-3">
      <TagGroup className="flex-wrap !gap-1">
        {user.memberRoles.map((role) => (
          <Tag key={role.roleId}>
            <span className="text-xs">{role.roleName}</span>
          </Tag>
        ))}
      </TagGroup>
      {editEnabled && (
        <ComposedPopover
          isOpen={manageRolesOpen}
          setIsOpen={setManageRolesOpen}
          trigger={<Button shape="square" appearance="subtle" size="small" icon={<Edit24Regular />} />}
        >
          <AddUserToWorkspaceForm
            selectedUser={user}
            projectId={projectId}
            orgId={orgId}
            appId={appId}
            onSubmit={closeManageRoles}
            workspaceId={workspaceId}
          />
        </ComposedPopover>
      )}
    </div>
  )

  return (
    <div className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-white p-4 shadow">
      {icon}
      {titleDescription}
      <ConfirmDialog
        trigger={<Button className="justify-self-end" shape="square" appearance="subtle" icon={<Delete24Regular />} />}
        title="Remove user from project"
        content="Please confirm that the user should be removed from the project."
        onConfirm={() => handleDeleteUser(user)}
      />
      {footer}
    </div>
  )
}
